
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Award, 
  Plus, 
  Search, 
  Loader2,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { LazyImage } from '@/components/ui/lazy-image';
import { toast } from 'sonner';
import { exportPremiumItineraryToPDF } from '@/utils/pdfExport';

export default function PremiumItineraries() {
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchPremiumItineraries = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('premium_itineraries')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        setItineraries(data || []);
      } catch (error) {
        console.error('Error fetching premium itineraries:', error);
        toast.error('Failed to load premium itineraries. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPremiumItineraries();
  }, []);

  // Placeholder function for filtering itineraries
  const filteredItineraries = itineraries.filter((itinerary: any) => 
    itinerary.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateDemoItinerary = async () => {
    try {
      setLoading(true);
      
      // Create a demo itinerary - Fixed the status type to be one of the allowed values
      const demoItinerary = {
        title: "Exploring the Greek Islands",
        destination: "Greek Islands, Greece",
        description: "Discover the beauty of the Greek Islands with this complete 10-day itinerary covering Santorini, Mykonos, and Crete.",
        best_season: "Spring, Summer, Fall",
        duration: 10,
        featured_image: "https://images.unsplash.com/photo-1530841377377-3ff06c0ca713",
        price: 29,
        currency: "USD",
        is_featured: true,
        status: "published" as "published" | "draft" | "archived" | "scheduled",
        slug: "exploring-the-greek-islands"
      };
      
      const { data, error } = await supabase
        .from('premium_itineraries')
        .insert(demoItinerary)
        .select();
      
      if (error) throw error;
      
      toast.success("Demo itinerary created successfully");
      
      // Refresh the list
      const { data: refreshedData, error: refreshError } = await supabase
        .from('premium_itineraries')
        .select('*')
        .order('created_at', { ascending: false });

      if (refreshError) throw refreshError;
      
      setItineraries(refreshedData || []);
    } catch (error: any) {
      console.error("Error creating demo itinerary:", error);
      toast.error(error.message || "Failed to create demo itinerary");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadItinerary = async (e: React.MouseEvent, itinerary: any) => {
    e.stopPropagation(); // Prevent navigation when clicking the download button
    
    try {
      setDownloadingId(itinerary.id);
      await exportPremiumItineraryToPDF(itinerary);
      toast.success("Itinerary downloaded successfully");
    } catch (error: any) {
      console.error("Error downloading itinerary:", error);
      toast.error("Failed to download itinerary");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Premium Itineraries</h1>
          <p className="text-muted-foreground">
            Manage your premium travel itineraries for sale.
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleCreateDemoItinerary}
            disabled={loading}
          >
            <Award className="mr-2 h-4 w-4" />
            Create Demo
          </Button>
          <Button onClick={() => navigate('/dashboard/premium-itineraries/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Premium Itinerary
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search premium itineraries..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredItineraries.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItineraries.map((itinerary: any) => (
            <Card 
              key={itinerary.id} 
              className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer relative"
              onClick={() => navigate(`/dashboard/premium-itineraries/${itinerary.id}`)}
            >
              <div className="aspect-video relative">
                <LazyImage
                  src={itinerary.featured_image || 'https://placehold.co/600x400/png'}
                  alt={itinerary.title}
                  className="object-cover w-full h-full"
                />
                <div className="absolute top-2 right-2">
                  <Badge variant={itinerary.status === 'published' ? 'default' : 'secondary'}>
                    {itinerary.status === 'published' ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                <div className="absolute top-2 left-2">
                  <Button
                    size="sm"
                    variant="secondary" 
                    className="bg-white/80 hover:bg-white text-gray-800"
                    onClick={(e) => handleDownloadItinerary(e, itinerary)}
                    disabled={downloadingId === itinerary.id}
                  >
                    {downloadingId === itinerary.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold">{itinerary.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {itinerary.destination}
                </p>
              </CardContent>
              <CardFooter className="border-t p-4">
                <div className="flex w-full justify-between text-sm">
                  <span>Price: {itinerary.price} {itinerary.currency}</span>
                  <span>Last edited: {new Date(itinerary.updated_at).toLocaleDateString()}</span>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border rounded-lg bg-background">
          <Award className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">No premium itineraries found</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {searchTerm ? `No results for "${searchTerm}"` : 'Get started by creating a new premium itinerary'}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-4">
            <Button 
              variant="outline" 
              onClick={handleCreateDemoItinerary}
              disabled={loading}
            >
              <Award className="mr-2 h-4 w-4" />
              Create Demo Itinerary
            </Button>
            <Button 
              onClick={() => navigate('/dashboard/premium-itineraries/new')}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Premium Itinerary
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
